import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { HttpFunctionsService } from '../http-services/http-functions.service';
import { CreateLyricsPayload, CreateLyricsBatchPayload, UpdateLyricsPayload } from 'src/app/models/lyrics.model';

@Injectable({ providedIn: 'root' })
export class LyricsService {
    private readonly BASE = 'lyrics/';

    /** Emits beat_id whenever lyrics are created, updated, or deleted */
    lyricsUpdated$ = new Subject<string>();

    constructor(private http: HttpFunctionsService) { }

    /** GET /lyrics/{beat_id} — all versions for a beat */
    getBeatLyrics(beatId: string): Observable<any> {
        return this.http.getMethod(this.BASE + beatId, false);
    }

    /** POST /lyrics/create — create single version */
    createLyrics(payload: CreateLyricsPayload): Observable<any> {
        return this.http.postMethod(this.BASE + 'create', payload, false);
    }

    /** POST /lyrics/create — batch create multiple versions in one call */
    createLyricsBatch(payload: CreateLyricsBatchPayload): Observable<any> {
        return this.http.postMethod(this.BASE + 'create', payload, false);
    }

    /** PUT /lyrics/update/{beat_id}/{version} */
    updateVersion(beatId: string, version: number, payload: UpdateLyricsPayload): Observable<any> {
        return this.http.putMethod(this.BASE + `update/${beatId}/${version}`, payload, false);
    }

    /** DELETE /lyrics/{beat_id}/{version} */
    deleteVersion(beatId: string, version: number): Observable<any> {
        return this.http.deleteMethod(this.BASE + `${beatId}/${version}`, null, false);
    }

    /** POST /lyrics/rhymes — get rhymes for a word (no loader) */
    getRhymes(word: string): Observable<any> {
        return this.http.postMethod(this.BASE + 'rhymes', { word }, false);
    }

    /** GET /lyrics/my-lyrics — list all lyrics for authenticated user */
    getMyLyrics(offset: number = 0, limit: number = 20): Observable<any> {
        return this.http.getMethod(this.BASE + `my-lyrics?offset=${offset}&limit=${limit}`);
    }

    /**
     * Convert the API's UTC date string (e.g. "Fri, February 20, 2026 at 08:03:14 AM UTC")
     * to IST (UTC+5:30) in a readable format.
     */
    convertUtcToIst(utcStr: string): string {
        try {
            let cleaned = utcStr
                .replace(/^[A-Za-z]+,\s*/, '')
                .replace(' at ', ' ')
                .replace(' UTC', ' GMT');

            const utcDate = new Date(cleaned);
            if (isNaN(utcDate.getTime())) return utcStr;

            const istDate = new Date(utcDate.getTime() + (5.5 * 60 * 60 * 1000));

            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = months[istDate.getUTCMonth()];
            const day = istDate.getUTCDate();
            const year = istDate.getUTCFullYear();
            let hours = istDate.getUTCHours();
            const minutes = istDate.getUTCMinutes().toString().padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12;

            return `${month} ${day}, ${year} ${hours}:${minutes} ${ampm} IST`;
        } catch {
            return utcStr;
        }
    }
}
